import { clamp, cos, exp, sin } from './dmath.ts';
import { ENEMY_SCALE, GATE, L, PITBOSS, RUNNER } from './config.ts';
import { DT, type Ctx } from './context.ts';
import { emergenceAt, laneAt, type RoutePoint } from './route.ts';
import { int, random } from './rng.ts';
import { NEVER, isBoss, type Enemy, type EnemyClass } from './state.ts';

/** Distance before the route's end (past the portal) at which an enemy counts as through the gate. */
const GATE_BREACH = 1.3;
/** Model variants per class (indices into the client's warrior templates). */
const CLS_TPL: Record<EnemyClass, number[]> = { soldier: [0, 1, 2], brute: [3], herald: [4], runner: [5], pitboss: [6] };
const GATE_COS = cos(L.gateAngle), GATE_SIN = sin(L.gateAngle);
const _p: RoutePoint = { x: 0, z: 0, dx: 0, dz: 0 };

export function spawn(ctx: Ctx, cls: EnemyClass, o: { s?: number; lane?: number; hp?: number; speed?: number } = {}): Enemy {
  const { st } = ctx, R = st.rng;
  const boss = isBoss(cls), fast = cls === 'runner', pit = cls === 'pitboss';
  const choices = CLS_TPL[cls], tpl = choices[int(st.looks, choices.length)];
  const lane = o.lane ?? (boss ? 1 + int(R, 2) : int(R, 4));
  const hp = o.hp ?? (pit ? PITBOSS.hp : boss ? 900 : fast ? RUNNER.hp : 60);
  const scale = ENEMY_SCALE * (pit ? PITBOSS.scale : boss ? (cls === 'brute' ? 1.28 : 1.12) : fast ? 0.57 : 0.55 + random(R) * 0.07);
  const s = o.s ?? 0;
  const laneZ = L.lanes[lane] + (random(R) - 0.5) * (boss ? 0.04 : 0.14);
  const speed = o.speed ?? (pit ? PITBOSS.speed : boss ? 0.32 : fast ? RUNNER.speed : 0.43 + random(R) * 0.27);
  const e: Enemy = {
    id: st.nextId++, cls, tpl, alive: true, leaked: false,
    s, prevS: s, lane, laneZ, side: 0, prevSide: 0, speed, hp, maxHp: hp, scale,
    radius: (pit ? PITBOSS.radius : boss ? 0.37 : fast ? 0.27 : 0.31) * scale, mass: pit ? PITBOSS.mass : boss ? 3 : fast ? 0.65 : 1,
    crowdX: 0, crowdZ: 0, pushX: 0, pushZ: 0, tangentX: 1, tangentZ: 0, bumpSide: 0, bumpForward: 0, reveal: 0,
    persona: random(R), stride: 0.82 + random(st.looks) * 0.4, swing: 0.75 + random(st.looks) * 0.55,
    frozen: 0, barrierPass: st.barrier && s + L.spawnX > st.barrier.x ? st.barrier.id : 0, attacking: 0, meleePhase: random(R),
    timeStop: 0, stasisAttack: 0,
    taunt: 0, tauntCd: boss ? 4 + random(R) * 6 : NEVER,
    hop: 0, hopCd: fast ? 2 + random(R) * 3 : NEVER, hopHeight: 0, hopDur: 0.45, dodge: 0,
    slow: 0, slowT: 0, mark: 0, poison: 0, poisonT: 0, poisonDps: 0, poisonOwner: 0, knock: 0, stage: 0,
  };
  st.enemies.push(e); ctx.byId.set(e.id, e);
  syncCrowdPosition(ctx, e);
  e.prevSide = e.side;
  e.reveal = emergenceAt(e.crowdX);
  return e;
}

export const routeLength = (ctx: Ctx, e: Enemy) => ctx.routes[e.lane].length;
/** World-space point to aim at (chest height). */
export const aimY = (e: Enemy) => 0.85 * e.scale;

/** Constrain the soft sideways motion to the road and the rotated gate opening. */
export function syncCrowdPosition(ctx: Ctx, e: Enemy) {
  laneAt(ctx.routes, e.lane, e.s, _p, e.laneZ);
  e.tangentX = _p.dx; e.tangentZ = _p.dz;
  const nx = -_p.dz, nz = _p.dx, c = GATE_COS, s = GATE_SIN;
  const gateV = (_p.x - L.gateX) * s + (_p.z - L.gateZ) * c;
  let low = -0.72, high = 0.72;
  if (gateV > e.radius + 0.25 && nz > 0.35) {
    const minZ = _p.x > L.cornerX - e.radius - 0.1 ? L.cornerZ + e.radius + 0.05 : L.roadZ - L.roadDepth / 2 + e.radius + 0.04;
    const maxZ = L.roadZ + L.roadDepth / 2 - e.radius - 0.04;
    low = Math.max(low, (minZ - _p.z) / nz); high = Math.min(high, (maxZ - _p.z) / nz);
  }
  const u = (_p.x - L.gateX) * c - (_p.z - L.gateZ) * s;
  const half = L.gateW / 2 - e.radius - 0.035, projection = nx * c - nz * s;
  // Intersect the road and doorway intervals; never pull a turning lane off the road.
  if (gateV < 1.25 && projection > 0.8) {
    const gateLow = Math.max(low, (-half - u) / projection), gateHigh = Math.min(high, (half - u) / projection);
    if (gateLow <= gateHigh) { low = gateLow; high = gateHigh; }
  }
  e.side = clamp(e.side, low, high);
  const v = gateV + (nx * s + nz * c) * e.side, across = u + projection * e.side;
  // On an early bend, the wall repels a sideways shove until the route aligns with the arch.
  if (gateV > 0 && v < e.radius + 0.28 && Math.abs(across) > half) {
    const front = nx * s + nz * c;
    if (Math.abs(front) > 0.1) {
      const limit = (e.radius + 0.28 - gateV) / front;
      e.side = clamp(front > 0 ? Math.max(e.side, limit) : Math.min(e.side, limit), low, high);
    }
  }
  e.crowdX = _p.x + nx * e.side; e.crowdZ = _p.z + nz * e.side;
}

const DECAY_SIDE = exp(-DT * 1.1), DECAY_SIDE_RUNNER = exp(-DT * 0.8), DECAY_DODGE = exp(-DT * 5), BUMP = 1 - exp(-DT * 12);

/** Fixed-step march. Enemies that reach the gate this tick leave the field. */
export function stepHorde(ctx: Ctx) {
  const { st } = ctx, dt = DT;
  for (const e of st.enemies) {
    e.prevS = e.s;
    e.prevSide = e.side;
    if (!e.alive) continue;
    e.mark = Math.max(0, e.mark - dt);
    e.slowT = Math.max(0, e.slowT - dt); if (e.slowT === 0) e.slow = Math.max(0, e.slow - dt * 3);
    const wasFrozen = e.frozen > 0;
    e.frozen = Math.max(0, e.frozen - dt);
    const wasStopped = e.timeStop > 0;
    e.timeStop = Math.max(0, e.timeStop - dt);
    if (wasStopped && e.timeStop === 0) ctx.ev.push({ type: 'time_resume', id: e.id });
    e.attacking = e.timeStop > 0 ? e.stasisAttack : 0;
    if (wasFrozen && e.frozen === 0) ctx.ev.push({ type: 'thaw', id: e.id });
    tauntStep(ctx, e, dt);
    if (e.cls === 'runner' && e.timeStop <= 0 && e.frozen <= 0) agility(ctx, e, dt);
    const paused = e.timeStop > 0, v = paused || e.frozen > 0 || e.taunt > 0 ? 0 : e.speed * (1 - e.slow * 0.55);
    e.s = Math.max(0, e.s + v * dt - (paused ? 0 : e.knock * dt)); e.knock = Math.max(0, e.knock - dt * 8);
    if (!paused) e.side *= e.cls === 'runner' ? DECAY_SIDE_RUNNER : DECAY_SIDE;
    syncCrowdPosition(ctx, e);
    blockAtBarrier(ctx, e, dt);
  }
  ctx.crowd.solve(st.enemies, dt);
  const inverseDt = 1 / dt;
  for (const e of st.enemies) {
    if (!e.alive) continue;
    // Project contact corrections onto progress and lateral motion, preserving the route.
    e.s = Math.max(0, e.s + e.pushX * e.tangentX + e.pushZ * e.tangentZ);
    e.side += -e.pushX * e.tangentZ + e.pushZ * e.tangentX;
    const lateral = -(e.pushX * e.tangentZ - e.pushZ * e.tangentX) * inverseDt * 0.065;
    const forward = (e.pushX * e.tangentX + e.pushZ * e.tangentZ) * inverseDt * 0.065;
    e.bumpSide += (clamp(-lateral, -0.10, 0.10) - e.bumpSide) * BUMP;
    e.bumpForward += (clamp(forward, -0.10, 0.10) - e.bumpForward) * BUMP;
    syncCrowdPosition(ctx, e);
    blockAtBarrier(ctx, e, 0);
    e.reveal = emergenceAt(e.crowdX);
    // the breach counts under the arch, in front of the portal
    if (e.s >= ctx.routes[e.lane].length - GATE_BREACH) {
      e.alive = false; e.leaked = true;
      ctx.st.stats.leaks++;
      ctx.ev.push({ type: 'leak', id: e.id, cls: e.cls });
      if (ctx.st.stats.leaks === GATE.breaches) ctx.ev.push({ type: 'gate_fallen', tick: ctx.st.tick });
    }
  }
}

/** Drop enemies that died or leaked; the renderer keeps its own reference for their final animation. */
export function sweepHorde(ctx: Ctx) {
  const list = ctx.st.enemies;
  let w = 0;
  for (let r = 0; r < list.length; r++) {
    const e = list[r];
    if (e.alive) list[w++] = e; else ctx.byId.delete(e.id);
  }
  list.length = w;
}

/**
 * Every 8–13 s a revealed boss halts, rears up and slams its weapon into the road,
 * reusing the melee swing the barrier drives (attacking + meleePhase).
 */
function tauntStep(ctx: Ctx, e: Enemy, dt: number) {
  if (!isBoss(e.cls) || e.frozen > 0 || e.timeStop > 0) return;
  if (e.taunt > 0) {
    const duration = 1.15, before = 1 - e.taunt / duration;
    e.taunt = Math.max(0, e.taunt - dt);
    const after = 1 - e.taunt / duration;
    e.meleePhase = after; e.attacking = e.taunt > 0 ? 1 : 0;
    if (before < 0.58 && after >= 0.58) ctx.ev.push({ type: 'slam', id: e.id });
    return;
  }
  const b = ctx.st.barrier;
  if (e.reveal < 0.9 || (b && e.crowdX > b.x - 1.2)) return;
  if ((e.tauntCd -= dt) <= 0) { e.taunt = 1.15; e.tauntCd = 8 + random(ctx.st.rng) * 5; e.meleePhase = 0; }
}

/** Runners hop now and then and side-step incoming spores. */
function agility(ctx: Ctx, e: Enemy, dt: number) {
  e.side += e.dodge * dt; e.dodge *= DECAY_DODGE;
  if (e.hop > 0) {
    e.hop += dt / e.hopDur;
    if (e.hop >= 1) { e.hop = 0; ctx.ev.push({ type: 'landing', id: e.id, high: e.hopHeight > 1 }); }
  } else if (e.reveal > 0.9 && (e.hopCd -= dt) <= 0) {
    e.hop = 1e-4; e.hopDur = 0.45; e.hopHeight = 0.32; e.hopCd = 3 + random(ctx.st.rng) * 4;
  }
}

function blockAtBarrier(ctx: Ctx, e: Enemy, dt: number) {
  const b = ctx.st.barrier;
  if (!b || b.hp <= 0 || e.barrierPass === b.id) return;
  if (e.timeStop > 0) return;
  const front = b.x - 0.24 - e.radius;
  // runners do not batter the wall: they vault it
  if (e.cls === 'runner') {
    if (e.crowdX >= front - 0.4) { e.barrierPass = b.id; e.hop = 1e-4; e.hopDur = 0.6; e.hopHeight = 1.35; }
    return;
  }
  if (e.crowdX < front - 0.055) return;
  e.s = Math.max(0, e.s - Math.max(0, e.crowdX - front) / Math.max(0.35, e.tangentX));
  syncCrowdPosition(ctx, e);
  e.attacking = e.frozen > 0 ? 0 : 1;
  if (!e.attacking || dt === 0) return;
  const previous = e.meleePhase;
  const period = isBoss(e.cls) ? 1.7 + e.persona * 0.5 : 0.95 + e.persona * 0.7;
  e.meleePhase = (previous + dt / period) % 1;
  if (previous < 0.58 && e.meleePhase >= 0.58) {
    b.hp = Math.max(0, b.hp - (isBoss(e.cls) ? 28 : 8));
    ctx.ev.push({ type: 'barrier_hit', z: e.crowdZ });
  }
}
